import type React from 'react'
import { useEffect, useState } from 'react'
import type { FileInProcessing, IOrder } from 'Model/index.js'
import { isFileInProcessing } from 'Model/index.js'
import { useDispatch } from 'react-redux'
import Actions from '../../../../Flux/index.js'
import { orderSlice } from '../../../../Flux/Slice/Order/OrderSlice.js'
import type { BaseModalProps } from '../../../Modal/BaseModal.js'
import { BaseModal } from '../../../Modal/BaseModal.js'
import { renderMessage } from './PipelineInformation/RenderMessage.js'
import { PreprocessingTab } from './PipelineInformation/PreprocessingTab.js'
import { QualityFilterLossWarning } from './PipelineInformation/QualityFilterLossWarning.tsx'
import { PostprocessingTab } from './PipelineInformation/PostprocessingTab.js'
import { OtuProcessingTab } from './PipelineInformation/OtuProcessingTab.js'
import { useGetIntermediatesStatusSelector, useGetSamplePoolSelector } from '../../../../Flux/Selector/index.js'
import { IntermediatesLine } from '../OrderDetail/IntermediatesNotice.tsx'
import {
  getEffectiveSequencingPlatform,
  getSequencingPlatformInlineBadgeStyle,
  getSequencingPlatformLabel
} from '../../../../Util/sequencingPlatform.ts'
import { SequencingPlatformEarlyAccessTag } from '../../../Component/SequencingPlatformEarlyAccessTag.tsx'

export interface PipelineInformationModalProps {
  order: IOrder
}

export const PipelineInformationModal: React.FC<PipelineInformationModalProps & BaseModalProps> = ({ order, onClose, isOpen }) => {
  const [poolItems, setPoolItems] = useState<FileInProcessing[]>([])
  const [showCheckboxes, setShowCheckboxes] = useState<boolean>(false)
  const dispatch = useDispatch()
  const samplePools = useGetSamplePoolSelector()
  const intermediates = useGetIntermediatesStatusSelector()

  useEffect(() => {
    if (samplePools !== 'LOADING') {
      const allFiles = samplePools.flatMap((sp) => [...sp.samples, ...sp.negativeControls])
      if (allFiles.map((x) => isFileInProcessing(x)).reduce((a, b) => a && b, true) == false) {
        throw new Error('Not all files are in processing state')
      }
      setPoolItems(allFiles.filter((i) => isFileInProcessing(i)))
    }
  }, [samplePools])

  useEffect(() => {
    if (isOpen) {
      dispatch(Actions[orderSlice.name].fetchPostprocessingData({ orderId: order.id }))
      if (order.orderDone) {
        dispatch(Actions[orderSlice.name].fetchOtuProcessingData({ orderId: order.id }))
      }
    }
  }, [isOpen, order])

  const effectiveSequencingPlatform = getEffectiveSequencingPlatform(order.detectedSequencingPlatform, order.sequencingPlatformOverride)

  return (
    <BaseModal isOpen={isOpen} onClose={onClose}>
      <div className="modal-box w-full max-w-5xl sm:max-w-[95vw] h-auto max-h-[90vh] overflow-auto p-4 sm:p-6">
        <button onClick={() => onClose()} className="btn btn-sm btn-circle btn-ghost absolute right-4 top-4 z-10" aria-label="Close Modal">
          ✕
        </button>
        <div className="p-6">
          <div className="flex items-center gap-2">
            <h3 className="text-lg font-bold">Pipeline Information</h3>
            {effectiveSequencingPlatform && (
              <span
                className={`inline-flex items-center rounded px-2 py-1 text-xs font-semibold ${getSequencingPlatformInlineBadgeStyle(effectiveSequencingPlatform)}`}
              >
                {getSequencingPlatformLabel(effectiveSequencingPlatform)}
                <SequencingPlatformEarlyAccessTag platform={effectiveSequencingPlatform} />
              </span>
            )}
          </div>
          <p className="mb-4">{renderMessage(showCheckboxes, order, poolItems)}</p>
          {!order.orderPostprocessingStarted && <QualityFilterLossWarning items={poolItems} />}
          <IntermediatesLine status={intermediates} />

          <div role="tablist" className="tabs tabs-border sm:max-md:tabs-xl">
            <input type="radio" name="my_tabs_1" role="tab" className="tab" aria-label="Preprocessing" defaultChecked />
            <div role="tabpanel" className="tab-content p-10">
              <PreprocessingTab
                order={order}
                poolItems={poolItems}
                showCheckboxes={showCheckboxes}
                setShowCheckboxes={setShowCheckboxes}
                onClose={onClose}
              />
            </div>

            {order.orderPostprocessingStarted && <PostprocessingTab order={order} onClose={onClose} />}
            {order.orderDone && <OtuProcessingTab order={order} onClose={onClose} />}
          </div>
        </div>
      </div>
    </BaseModal>
  )
}
interface CollapsibleSpanProps {
  maxChars: number
  content: string
  collapsedContent?: string
  expanded?: boolean
  onExpand?: (state: boolean) => void
}

export const CollapsibleSpan: React.FC<CollapsibleSpanProps> = ({ maxChars, content, collapsedContent, expanded, onExpand }) => {
  const [currentText, setCurrentText] = useState('')
  const [isExpanded, setIsExpanded] = useState<boolean>(false)

  useEffect(() => {
    if (expanded !== undefined) {
      setIsExpanded(expanded)
    }
  }, [expanded])

  useEffect(() => {
    if (isExpanded || content.length <= maxChars) {
      setCurrentText(content)
    } else {
      const baseText = collapsedContent ?? content
      setCurrentText(baseText.length > maxChars ? baseText.substring(0, maxChars) + '...' : baseText)
    }
  }, [content, maxChars, isExpanded, collapsedContent])

  const toggleExpanded = () => {
    if (onExpand) {
      onExpand(!isExpanded)
    }
    setIsExpanded(!isExpanded)
  }

  return (
    <span style={content.length > maxChars ? { cursor: 'pointer' } : {}} onClick={toggleExpanded}>
      {currentText}
    </span>
  )
}
